import React, { useEffect, useState, useCallback } from 'react';
import { reclamosAdminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Loader2, Search, Paperclip, X, Download, Inbox,
  ArrowDownUp, Lock, Send,
} from 'lucide-react';

const ESTADOS = ['nuevo', 'en_revision', 'respondido', 'cerrado'];
const ESTADO_LABEL = { nuevo: 'Nuevo', en_revision: 'En revisión', respondido: 'Respondido', cerrado: 'Cerrado' };
const ESTADO_STYLE = {
  nuevo: 'bg-blue-50 text-blue-700 border-blue-200',
  en_revision: 'bg-amber-50 text-amber-700 border-amber-200',
  respondido: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cerrado: 'bg-slate-100 text-slate-500 border-slate-200',
};

const fmtFecha = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const EstadoBadge = ({ estado }) => (
  <span data-testid={`estado-badge-${estado}`} className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${ESTADO_STYLE[estado] || ESTADO_STYLE.nuevo}`}>
    {ESTADO_LABEL[estado] || estado}
  </span>
);

const TODOS = '__todos__';

export default function RevisorReclamos() {
  const [reclamos, setReclamos] = useState([]);
  const [meta, setMeta] = useState({ counts: {}, propietarios: [], edificios: [] });
  const [loading, setLoading] = useState(true);
  const [detalleId, setDetalleId] = useState(null);

  const [fEstado, setFEstado] = useState(TODOS);
  const [fEdificio, setFEdificio] = useState(TODOS);
  const [fPropietario, setFPropietario] = useState(TODOS);
  const [fUnidad, setFUnidad] = useState(TODOS);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('desc');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { orden };
      if (fEstado !== TODOS) params.estado = fEstado;
      if (fEdificio !== TODOS) params.edificio_id = fEdificio;
      if (fPropietario !== TODOS) params.propietario_id = fPropietario;
      if (fUnidad !== TODOS) params.unidad_id = fUnidad;
      if (busqueda.trim()) params.q = busqueda.trim();
      const r = await reclamosAdminApi.list(params);
      setReclamos(r.data || []);
    } catch {
      toast.error('No se pudieron cargar los reclamos');
    } finally { setLoading(false); }
  }, [orden, fEstado, fEdificio, fPropietario, fUnidad, busqueda]);

  const fetchMeta = useCallback(async () => {
    try { const r = await reclamosAdminApi.meta(); setMeta(r.data || {}); } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => {
    const t = setTimeout(fetchList, busqueda ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchList, busqueda]);

  // Unidades distintas dentro del set cargado (filtro client-side)
  const unidadesOpts = Array.from(
    new Map(reclamos.filter((r) => r.unidad_id).map((r) => [r.unidad_id, r.unidad_nombre])).entries()
  ).map(([value, label]) => ({ value, label }));

  const onChanged = () => { fetchList(); fetchMeta(); };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-indigo-700" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Reclamos y Sugerencias</h1>
          <p className="text-slate-500 mt-1">Bandeja de reclamos, consultas y sugerencias de propietarios</p>
        </div>
      </div>

      {/* Resumen por estado */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {[['total', 'Total'], ...ESTADOS.map((e) => [e, ESTADO_LABEL[e]])].map(([k, label]) => (
          <button
            key={k}
            data-testid={`kpi-${k}`}
            onClick={() => setFEstado(k === 'total' ? TODOS : k)}
            className={`rounded-lg border p-3 text-left transition hover:shadow-sm ${
              (k === 'total' && fEstado === TODOS) || fEstado === k ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="text-xl font-bold text-slate-900">{meta.counts?.[k] ?? 0}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por asunto o texto…"
            className="pl-8" data-testid="reclamos-busqueda" />
        </div>
        <Select value={fEdificio} onValueChange={setFEdificio}>
          <SelectTrigger className="w-auto min-w-[150px]" data-testid="filtro-edificio"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos los edificios</SelectItem>
            {(meta.edificios || []).map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fPropietario} onValueChange={setFPropietario}>
          <SelectTrigger className="w-auto min-w-[150px]" data-testid="filtro-propietario"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos los propietarios</SelectItem>
            {(meta.propietarios || []).map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fUnidad} onValueChange={setFUnidad}>
          <SelectTrigger className="w-auto min-w-[130px]" data-testid="filtro-unidad"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todas las unidades</SelectItem>
            {unidadesOpts.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => setOrden((o) => (o === 'desc' ? 'asc' : 'desc'))} data-testid="orden-toggle">
          <ArrowDownUp className="w-4 h-4 mr-1" /> {orden === 'desc' ? 'Más nuevos' : 'Más viejos'}
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : reclamos.length === 0 ? (
        <div className="py-16 flex flex-col items-center text-slate-400 bg-white border border-slate-200 rounded-lg">
          <Inbox className="w-10 h-10 mb-2" />
          <p className="text-sm">No hay reclamos que coincidan con los filtros.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="reclamos-admin-list">
          {reclamos.map((r) => (
            <button
              key={r.reclamo_id}
              onClick={() => setDetalleId(r.reclamo_id)}
              data-testid={`reclamo-row-${r.reclamo_id}`}
              className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-sm transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-medium text-indigo-600 uppercase">{r.tipo_label}</span>
                    <span className="text-[11px] text-slate-400">· {r.tema_label}</span>
                  </div>
                  <div className="font-medium text-slate-900 truncate">{r.asunto}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.propietario_nombre} · {r.edificio_nombre} · {r.unidad_nombre} · {fmtFecha(r.created_at)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <EstadoBadge estado={r.estado} />
                  {r.comentarios_count > 0 && (
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> {r.comentarios_count}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {detalleId && (
        <DetalleDialog reclamoId={detalleId} onClose={() => setDetalleId(null)} onChanged={onChanged} />
      )}
    </div>
  );
}

// ─────────────── Adjunto link (descarga via blob) ───────────────
const AdjuntoLink = ({ a }) => {
  const [busy, setBusy] = useState(false);
  const abrir = async () => {
    setBusy(true);
    try {
      const r = await reclamosAdminApi.download(a.file_id);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('No se pudo abrir el adjunto'); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={abrir} disabled={busy} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline" data-testid={`adjunto-${a.file_id}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      <span className="max-w-[160px] truncate">{a.original_filename}</span>
    </button>
  );
};

// ─────────────── Detalle ───────────────
const DetalleDialog = ({ reclamoId, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [visibilidad, setVisibilidad] = useState('publico');
  const [adjuntos, setAdjuntos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingEstado, setSavingEstado] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await reclamosAdminApi.detalle(reclamoId); setDoc(r.data); }
    catch { toast.error('No se pudo cargar el detalle'); onClose(); }
    finally { setLoading(false); }
  }, [reclamoId, onClose]);

  useEffect(() => { fetch(); }, [fetch]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await reclamosAdminApi.upload(fd);
      setAdjuntos((p) => [...p, r.data]);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo subir'); }
    finally { setUploading(false); }
  };

  const enviar = async () => {
    if (!texto.trim()) return;
    setSending(true);
    try {
      await reclamosAdminApi.comentar(reclamoId, { texto: texto.trim(), visibilidad, adjuntos: adjuntos.map((a) => a.file_id) });
      setTexto(''); setAdjuntos([]);
      toast.success(visibilidad === 'interno' ? 'Nota interna agregada' : 'Respuesta enviada al propietario');
      await fetch(); onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo enviar'); }
    finally { setSending(false); }
  };

  const cambiarEstado = async (estado) => {
    setSavingEstado(true);
    try {
      await reclamosAdminApi.cambiarEstado(reclamoId, estado);
      await fetch(); onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo cambiar el estado'); }
    finally { setSavingEstado(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="reclamo-detalle-dialog">
        {loading || !doc ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-medium text-indigo-600 uppercase">{doc.tipo_label}</span>
                <EstadoBadge estado={doc.estado} />
              </div>
              <DialogTitle className="text-base">{doc.asunto}</DialogTitle>
            </DialogHeader>

            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div><span className="text-slate-400">Propietario:</span> {doc.propietario_nombre}</div>
                <div><span className="text-slate-400">Edificio:</span> {doc.edificio_nombre}</div>
                <div><span className="text-slate-400">Unidad:</span> {doc.unidad_nombre}</div>
                <div><span className="text-slate-400">Creado:</span> {fmtFecha(doc.created_at)}</div>
                <div><span className="text-slate-400">Tema:</span> {doc.tema_label}</div>
                <div><span className="text-slate-400">Motivo:</span> {doc.motivo}</div>
              </div>
              <div className="bg-slate-50 rounded-md p-3 whitespace-pre-wrap text-slate-800">{doc.descripcion}</div>
              {doc.adjuntos?.length > 0 && (
                <div className="flex flex-wrap gap-2">{doc.adjuntos.map((a) => <AdjuntoLink key={a.file_id} a={a} />)}</div>
              )}

              {/* Estado */}
              <div className="border-t border-slate-100 pt-3">
                <div className="text-xs font-medium text-slate-600 mb-1.5">Cambiar estado</div>
                <div className="flex flex-wrap gap-1.5">
                  {ESTADOS.map((e) => (
                    <Button key={e} size="sm" disabled={savingEstado || doc.estado === e}
                      variant={doc.estado === e ? 'default' : 'outline'}
                      onClick={() => cambiarEstado(e)} data-testid={`set-estado-${e}`}>
                      {ESTADO_LABEL[e]}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Historial */}
              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-2">
                  <MessageSquare className="w-3.5 h-3.5" /> Historial de seguimiento ({doc.comentarios?.length || 0})
                </div>
                {(!doc.comentarios || doc.comentarios.length === 0) && (
                  <p className="text-xs text-slate-400">Sin comentarios aún.</p>
                )}
                <div className="space-y-2">
                  {(doc.comentarios || []).map((c) => {
                    const esProp = c.autor_tipo === 'propietario';
                    const interno = c.visibilidad === 'interno';
                    return (
                      <div key={c.comentario_id} data-testid={`comentario-${c.comentario_id}`}
                        className={`rounded-md p-2.5 border ${interno ? 'bg-amber-50 border-amber-200' : esProp ? 'bg-indigo-50 border-indigo-100' : 'bg-emerald-50 border-emerald-100'}`}>
                        <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                          <span className="font-medium flex items-center gap-1">
                            {c.autor_nombre}{!esProp ? ' · Equipo' : ' · Propietario'}
                            {interno && <span className="inline-flex items-center gap-0.5 text-amber-700"><Lock className="w-3 h-3" /> Nota interna</span>}
                          </span>
                          <span>{fmtFecha(c.created_at)}</span>
                        </div>
                        <div className="text-slate-800 whitespace-pre-wrap">{c.texto}</div>
                        {c.adjuntos?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-1.5">{c.adjuntos.map((a) => <AdjuntoLink key={a.file_id} a={a} />)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Responder */}
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Select value={visibilidad} onValueChange={setVisibilidad}>
                    <SelectTrigger className="w-auto min-w-[210px]" data-testid="visibilidad-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="publico">Respuesta al propietario</SelectItem>
                      <SelectItem value="interno">Comentario interno (solo equipo)</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="inline-flex items-center gap-1.5 text-sm text-indigo-600 cursor-pointer hover:underline">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Adjuntar
                    <input type="file" accept="image/*,.pdf" onChange={onPick} className="hidden" disabled={uploading} data-testid="comentario-file-input" />
                  </label>
                </div>
                {adjuntos.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {adjuntos.map((a) => (
                      <span key={a.file_id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded px-2 py-1">
                        <span className="max-w-[140px] truncate">{a.original_filename}</span>
                        <button type="button" onClick={() => setAdjuntos((p) => p.filter((x) => x.file_id !== a.file_id))}>
                          <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3}
                  placeholder={visibilidad === 'interno' ? 'Nota interna (no la ve el propietario)…' : 'Escribí la respuesta al propietario…'}
                  data-testid="comentario-texto" />
                <div className="flex justify-end">
                  <Button size="sm" onClick={enviar} disabled={sending || !texto.trim()} data-testid="comentario-submit">
                    {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                    {visibilidad === 'interno' ? 'Guardar nota' : 'Responder'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
